import type { InputState, MatchConfig, RosterPlayer, WorldState } from "../game/Types";

const INPUT_PACKET = 1;
const MAX_PLAYER_ID_BYTES = 48;
const FRAME_BYTES = 9;
export const MAX_INPUT_FRAMES = 16;
export const MAX_CONTROL_BYTES = 128 * 1024;
const MAX_PLAYERS = 8;
const MAX_PROJECTILES = 512;
const MAX_DESTRUCTIBLES = 128;
const MAX_PICKUPS = 128;

export interface InputFramePacket {
  playerId: string;
  frameNumber: number;
  inputState: InputState;
  timestamp: number;
}

export type ControlMessage =
  | { type: "match-start"; config: MatchConfig; roster: RosterPlayer[]; hostId: string }
  | { type: "ping"; id: number; sentAt: number }
  | { type: "pong"; id: number; sentAt: number }
  | { type: "checksum"; frame: number; checksum: number }
  | { type: "resync-request"; frame: number; requesterId: string }
  | { type: "snapshot"; frame: number; state: WorldState }
  | { type: "host-migrated"; hostId: string }
  | { type: "peer-leaving"; peerId: string };

export const encodeInputPacket = (frames: InputFramePacket[]): ArrayBuffer => {
  if (frames.length === 0 || frames.length > MAX_INPUT_FRAMES) throw new Error("Input packet frame count is out of range.");
  const playerBytes = new TextEncoder().encode(frames[0]!.playerId).slice(0, MAX_PLAYER_ID_BYTES);
  const buffer = new ArrayBuffer(8 + playerBytes.length + frames.length * FRAME_BYTES);
  const view = new DataView(buffer);
  view.setUint8(0, INPUT_PACKET);
  view.setUint8(1, playerBytes.length);
  view.setUint8(2, frames.length);
  view.setUint8(3, 0);
  view.setUint32(4, frames[0]!.timestamp >>> 0);
  new Uint8Array(buffer, 8, playerBytes.length).set(playerBytes);

  let offset = 8 + playerBytes.length;
  for (const frame of frames) {
    view.setUint32(offset, frame.frameNumber >>> 0);
    view.setUint16(offset + 4, frame.inputState.bits & 0xffff);
    view.setInt8(offset + 6, clampInt8(frame.inputState.aimX));
    view.setInt8(offset + 7, clampInt8(frame.inputState.aimY));
    view.setUint8(offset + 8, frame.inputState.weapon & 0xff);
    offset += FRAME_BYTES;
  }
  return buffer;
};

export const decodeInputPacket = (buffer: ArrayBuffer): InputFramePacket[] => {
  if (buffer.byteLength < 8) return [];
  const view = new DataView(buffer);
  if (view.getUint8(0) !== INPUT_PACKET) return [];
  const idLength = view.getUint8(1);
  const frameCount = view.getUint8(2);
  const requiredLength = 8 + idLength + frameCount * FRAME_BYTES;
  if (idLength > MAX_PLAYER_ID_BYTES || frameCount > MAX_INPUT_FRAMES || buffer.byteLength !== requiredLength) return [];
  const playerId = new TextDecoder().decode(new Uint8Array(buffer, 8, idLength));
  const timestamp = view.getUint32(4);
  const frames: InputFramePacket[] = [];
  let offset = 8 + idLength;
  for (let index = 0; index < frameCount; index += 1) {
    frames.push({
      playerId,
      timestamp,
      frameNumber: view.getUint32(offset),
      inputState: {
        bits: view.getUint16(offset + 4),
        aimX: view.getInt8(offset + 6),
        aimY: view.getInt8(offset + 7),
        weapon: view.getUint8(offset + 8),
      },
    });
    offset += FRAME_BYTES;
  }
  return frames;
};

/** Parse a bounded, allow-listed control packet received from an untrusted peer. */
export const decodeControlMessage = (value: string): ControlMessage | null => {
  if (value.length > MAX_CONTROL_BYTES) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(value) as unknown;
  } catch {
    return null;
  }
  if (!isRecord(parsed) || typeof parsed.type !== "string" || !CONTROL_TYPES.has(parsed.type)) return null;
  if (parsed.type === "ping" || parsed.type === "pong") {
    return isFiniteNumber(parsed.id) && isFiniteNumber(parsed.sentAt) ? parsed as ControlMessage : null;
  }
  if (parsed.type === "checksum") {
    return isSafeFrame(parsed.frame) && isFiniteNumber(parsed.checksum) ? parsed as ControlMessage : null;
  }
  if (parsed.type === "resync-request") {
    return isSafeFrame(parsed.frame) && boundedString(parsed.requesterId) ? parsed as ControlMessage : null;
  }
  if (parsed.type === "peer-leaving" || parsed.type === "host-migrated") {
    return boundedString(parsed.type === "peer-leaving" ? parsed.peerId : parsed.hostId)
      ? parsed as ControlMessage : null;
  }
  if (parsed.type === "match-start") {
    return isRecord(parsed.config) && Array.isArray(parsed.roster)
      && parsed.roster.length > 0 && parsed.roster.length <= 8
      && boundedString(parsed.hostId)
      ? parsed as ControlMessage : null;
  }
  if (parsed.type === "snapshot") {
    return isSafeFrame(parsed.frame) && isWorldState(parsed.state) && parsed.frame === parsed.state.frame
      ? parsed as ControlMessage : null;
  }
  return null;
};

export const sameInput = (left: InputState | undefined, right: InputState | undefined): boolean => {
  return left?.bits === right?.bits && left?.aimX === right?.aimX
    && left?.aimY === right?.aimY && left?.weapon === right?.weapon;
};

const clampInt8 = (value: number): number => Math.max(-127, Math.min(127, Math.round(value)));

const CONTROL_TYPES = new Set([
  "match-start",
  "ping",
  "pong",
  "checksum",
  "resync-request",
  "snapshot",
  "host-migrated",
  "peer-leaving",
]);

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;
const isFiniteNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);
const isSafeFrame = (value: unknown): value is number => Number.isSafeInteger(value) && (value as number) >= 0 && (value as number) <= 0x7fffffff;
const boundedString = (value: unknown): value is string => typeof value === "string" && value.length > 0 && value.length <= MAX_PLAYER_ID_BYTES;
const isWeapon = (value: unknown): boolean => typeof value === "string" && WEAPONS.has(value);
const isTeam = (value: unknown): boolean => value === "ember" || value === "azure" || value === "none";
const isGender = (value: unknown): boolean => value === "male" || value === "female";

const isWorldState = (value: unknown): value is WorldState => {
  if (!isRecord(value)
    || !isSafeFrame(value.frame)
    || !isFiniteNumber(value.rng)
    || !Number.isSafeInteger(value.nextEntityId)
    || !Array.isArray(value.projectiles) || value.projectiles.length > MAX_PROJECTILES
    || !Array.isArray(value.destructibles) || value.destructibles.length > MAX_DESTRUCTIBLES
    || !Array.isArray(value.pickups) || value.pickups.length > MAX_PICKUPS
    || (value.winnerId !== null && !boundedString(value.winnerId))) return false;
  if (!isRecord(value.players) || Array.isArray(value.players)) return false;
  const players = value.players;
  const playerIds = Object.keys(players);
  if (playerIds.length === 0 || playerIds.length > MAX_PLAYERS || playerIds.some((id) => !boundedString(id))) return false;
  return playerIds.every((id) => isPlayerState(players[id]))
    && value.projectiles.every(isProjectileState)
    && value.destructibles.every(isDestructibleState)
    && value.pickups.every(isPickupState);
};

const isPlayerState = (value: unknown): boolean => {
  if (!isRecord(value) || Array.isArray(value)
    || !boundedString(value.id) || typeof value.name !== "string" || value.name.length > 128
    || !isGender(value.gender) || !isTeam(value.team)
    || !isFiniteNumber(value.x) || !isFiniteNumber(value.y) || !isFiniteNumber(value.vx) || !isFiniteNumber(value.vy)
    || (value.facing !== -1 && value.facing !== 1) || !isFiniteNumber(value.health) || !isWeapon(value.weapon)
    || !isFiniteNumber(value.reloadFrames) || !isFiniteNumber(value.regenDelayFrames) || !isFiniteNumber(value.regenTickFrames)
    || typeof value.grounded !== "boolean" || !isFiniteNumber(value.jumpsUsed) || !isFiniteNumber(value.dashFrames)
    || !isFiniteNumber(value.dashCooldown) || !isFiniteNumber(value.shootCooldown) || !isFiniteNumber(value.meleeCooldown)
    || !isFiniteNumber(value.invulnerableFrames) || !isFiniteNumber(value.respawnFrames)
    || !isFiniteNumber(value.kills) || !isFiniteNumber(value.deaths) || !isFiniteNumber(value.score)
    || !isFiniteNumber(value.inputLatch)) return false;
  if (!isRecord(value.ammo) || Array.isArray(value.ammo)) return false;
  const ammo = value.ammo;
  if (![...WEAPONS].some((weapon) => !isFiniteNumber(ammo[weapon]))) return false;
  return true;
};

const isProjectileState = (value: unknown): boolean => isRecord(value) && !Array.isArray(value)
  && Number.isSafeInteger(value.id) && boundedString(value.ownerId) && isTeam(value.team) && isWeapon(value.weapon)
  && isFiniteNumber(value.x) && isFiniteNumber(value.y) && isFiniteNumber(value.vx) && isFiniteNumber(value.vy)
  && isFiniteNumber(value.ttl) && isFiniteNumber(value.damage) && isFiniteNumber(value.radius)
  && isFiniteNumber(value.bounces);

const isDestructibleState = (value: unknown): boolean => isRecord(value) && !Array.isArray(value)
  && boundedString(value.id) && isFiniteNumber(value.x) && isFiniteNumber(value.y)
  && isFiniteNumber(value.width) && isFiniteNumber(value.height)
  && isFiniteNumber(value.health) && isFiniteNumber(value.maxHealth);

const isPickupState = (value: unknown): boolean => isRecord(value) && !Array.isArray(value)
  && boundedString(value.id) && (value.kind === "health" || isWeapon(value.kind))
  && isFiniteNumber(value.x) && isFiniteNumber(value.y)
  && typeof value.active === "boolean" && isFiniteNumber(value.respawnFrames);

const WEAPONS = new Set(["pistol", "shotgun", "machineGun", "rocket", "grenade"]);
